import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Compass, GraduationCap, Users, FileText, CheckCircle, 
  Video, BarChart3, Shield, Globe, Mail, Phone, Building,
  ArrowRight, Play, Star, Clock, Zap
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const CollegeDemo = () => {
  const [formData, setFormData] = useState({
    college_name: '',
    contact_name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // In production, this would send to a backend endpoint
      // For now, we'll simulate success
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSubmitted(true);
      toast({
        title: 'Request Submitted!',
        description: 'We will contact you within 24 hours.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    {
      icon: FileText,
      title: 'Streamlined Applications',
      description: 'Students complete one application for multiple colleges, reducing errors and improving completion rates.'
    },
    {
      icon: Users,
      title: 'Counselor Portal',
      description: 'Dedicated portal for school counselors to manage transcripts, recommendations, and track student progress.'
    },
    {
      icon: Video,
      title: 'AI Interview Prep',
      description: 'Built-in AI-powered interview preparation helps students present their best selves.'
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Real-time insights into application volumes, demographics, and conversion metrics.'
    },
    {
      icon: Shield,
      title: 'Secure & Compliant',
      description: 'FERPA compliant with enterprise-grade security for student data protection.'
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Access to a diverse pool of domestic and international applicants.'
    }
  ];

  const stats = [
    { value: '920+', label: 'Member Colleges' },
    { value: '1M+', label: 'Applications/Year' },
    { value: '98%', label: 'Completion Rate' },
    { value: '50+', label: 'Countries' }
  ];

  const testimonials = [
    {
      quote: "Wayo has transformed our admissions process. The quality of applications has improved significantly.",
      author: "Dr. Sarah Johnson",
      role: "Dean of Admissions",
      college: "State University"
    },
    {
      quote: "The counselor portal makes it easy to support our students through the entire application journey.",
      author: "Michael Chen",
      role: "High School Counselor",
      college: "Lincoln High School"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-500 p-2 rounded-xl">
              <Compass className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Wayo
            </span>
            <Badge variant="outline" className="ml-2">For Colleges</Badge>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-gray-600 hover:text-gray-900">Student Portal</a>
            <a href="/counselor" className="text-gray-600 hover:text-gray-900">Counselor Portal</a>
            <Button onClick={() => document.getElementById('demo-form').scrollIntoView({ behavior: 'smooth' })}>
              Request Demo
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        <div className="max-w-7xl mx-auto px-4 py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-white/20 text-white border-0 mb-6">
                <Star className="h-3 w-3 mr-1" /> Trusted by 920+ Colleges
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                The Modern College Application Platform
              </h1>
              <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
                Join Wayo to receive high-quality applications from motivated students worldwide. 
                Our platform streamlines admissions while providing rich insights into your applicant pool.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-white text-emerald-600 hover:bg-gray-100"
                  onClick={() => document.getElementById('demo-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Schedule a Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => window.open('/', '_blank')}
                >
                  <Play className="mr-2 h-5 w-5" />
                  See Student Experience
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <img 
                  src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=600&h=400&fit=crop" 
                  alt="Students on campus"
                  className="rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl font-bold text-emerald-600">{stat.value}</p>
                <p className="text-gray-600 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4">Platform Features</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need for Modern Admissions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wayo provides a comprehensive suite of tools to help you attract, evaluate, and enroll the best students.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-2 hover:border-emerald-200 transition-colors">
                <CardHeader>
                  <div className="bg-emerald-100 p-3 rounded-xl w-fit mb-4">
                    <feature.icon className="h-6 w-6 text-emerald-600" />
                  </div>
                  <CardTitle>{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4">How It Works</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Simple Integration Process
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Sign Up', desc: 'Complete our college membership form' },
              { step: '2', title: 'Configure', desc: 'Set up your requirements and deadlines' },
              { step: '3', title: 'Go Live', desc: 'Start receiving applications' },
              { step: '4', title: 'Review', desc: 'Use our tools to evaluate applicants' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="bg-emerald-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Demo Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4">Live Demo</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Experience the Platform
            </h2>
            <p className="text-xl text-gray-600">
              Explore our demo portals to see how Wayo works
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-emerald-300 transition-all hover:shadow-lg">
              <CardHeader className="text-center">
                <div className="bg-gradient-to-br from-emerald-500 to-teal-500 p-4 rounded-2xl w-fit mx-auto mb-4">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <CardTitle>Student Portal</CardTitle>
                <CardDescription>See the student application experience</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button className="w-full" onClick={() => window.open('/', '_blank')}>
                  View Demo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-sm text-gray-500 mt-3">
                  Demo: test@demo.com / Demo123!
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-purple-300 transition-all hover:shadow-lg">
              <CardHeader className="text-center">
                <div className="bg-gradient-to-br from-purple-500 to-indigo-500 p-4 rounded-2xl w-fit mx-auto mb-4">
                  <GraduationCap className="h-8 w-8 text-white" />
                </div>
                <CardTitle>Counselor Portal</CardTitle>
                <CardDescription>Manage students and transcripts</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button className="w-full bg-purple-600 hover:bg-purple-700" onClick={() => window.open('/counselor', '_blank')}>
                  View Demo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-sm text-gray-500 mt-3">
                  Demo: counselor@school.edu / Counselor123!
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-cyan-300 transition-all hover:shadow-lg">
              <CardHeader className="text-center">
                <div className="bg-gradient-to-br from-cyan-500 to-blue-500 p-4 rounded-2xl w-fit mx-auto mb-4">
                  <Video className="h-8 w-8 text-white" />
                </div>
                <CardTitle>AI Interview Prep</CardTitle>
                <CardDescription>AI-powered visa interview practice</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button className="w-full bg-cyan-600 hover:bg-cyan-700" onClick={() => window.open('/interview', '_blank')}>
                  View Demo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-sm text-gray-500 mt-3">
                  Requires student login
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4">Testimonials</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              What Our Partners Say
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-lg text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold">{testimonial.author}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}, {testimonial.college}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Request Form */}
      <section id="demo-form" className="py-20 bg-gradient-to-br from-emerald-600 to-teal-600">
        <div className="max-w-3xl mx-auto px-4">
          <Card className="shadow-2xl">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Request a Demo</CardTitle>
              <CardDescription>
                Fill out the form below and our team will contact you within 24 hours
              </CardDescription>
            </CardHeader>
            <CardContent>
              {submitted ? (
                <div className="text-center py-8">
                  <div className="bg-green-100 p-4 rounded-full w-fit mx-auto mb-4">
                    <CheckCircle className="h-12 w-12 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
                  <p className="text-gray-600">
                    Your demo request has been submitted. Our team will contact you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="college_name">College/University Name *</Label>
                      <div className="relative mt-1">
                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="college_name"
                          value={formData.college_name}
                          onChange={(e) => setFormData({...formData, college_name: e.target.value})}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="contact_name">Your Name *</Label>
                      <div className="relative mt-1">
                        <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="contact_name"
                          value={formData.contact_name}
                          onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <div className="relative mt-1">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">Message (Optional)</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Tell us about your institution and what you're looking for..."
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Request Demo'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="bg-gradient-to-br from-emerald-500 to-teal-500 p-2 rounded-xl">
                  <Compass className="h-5 w-5 text-white" />
                </div>
                <span className="text-xl font-bold">Wayo</span>
              </div>
              <p className="text-gray-400">Your Way Forward</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Platform</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/" className="hover:text-white">Student Portal</a></li>
                <li><a href="/counselor" className="hover:text-white">Counselor Portal</a></li>
                <li><a href="/demo" className="hover:text-white">For Colleges</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Help Center</a></li>
                <li><a href="#" className="hover:text-white">Documentation</a></li>
                <li><a href="#" className="hover:text-white">API</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>support@wayo.edu</li>
                <li>1-800-WAYO-APP</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>© 2026 Wayo. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CollegeDemo;
